import { classNames, Dropzone, humanizeFileSize, useSnackbar } from "@deathstar/ui";
import { ArrowUpTrayIcon, PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useRef } from "react";
import api from "../../api/api";

const ACCEPTED_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg", ".doc", ".docx"];
const MAX_FILES = 10;
const MAX_FILE_BYTES = 1024 * 1024 * 30;

/** Drag-and-drop picker for documents that support an equipment change, e.g. a lease termination or rental agreement */
export function SupportingDocumentsInput({
    id,
    value,
    onChange,
    hint,
}: {
    id: string;
    value: File[];
    onChange(files: File[]): void;
    /** Example documents shown to the client, e.g. "Bill of Sale, Lease termination document, etc." */
    hint?: string;
}) {
    const fileInput = useRef<HTMLInputElement>(null);

    const addFiles = (files: File[]) => {
        const accepted = files.filter(
            (file) => ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext)) && file.size <= MAX_FILE_BYTES
        );
        const next = [...value, ...accepted.filter((file) => !value.some((v) => v.name === file.name && v.size === file.size))];
        if (accepted.length < files.length) {
            useSnackbar.add("Only PDF, image, or Word documents up to 30 MB can be added.", { variant: "warning" });
        }
        if (next.length > MAX_FILES) {
            useSnackbar.add(`You can add up to ${MAX_FILES} documents.`, { variant: "warning" });
        }
        onChange(next.slice(0, MAX_FILES));
        if (fileInput.current) {
            fileInput.current.value = "";
        }
    };

    return (
        <div className="space-y-2">
            <Dropzone id={id} onDrop={(files) => addFiles(files)}>
                {({ getProps, isDragActive, handleFileSelect }) => (
                    <div
                        {...getProps()}
                        className={classNames(
                            "flex flex-col items-center gap-1 rounded-lg border-2 border-dashed p-6 text-center text-sm transition-colors",
                            isDragActive ? "border-blue-600 bg-blue-50" : "border-stone-300"
                        )}
                    >
                        <input
                            ref={fileInput}
                            type="file"
                            multiple
                            accept={ACCEPTED_EXTENSIONS.join(",")}
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                        <ArrowUpTrayIcon className="h-6 w-6 text-stone-400" />
                        <p className="text-stone-600">
                            Drag and drop files here, or{" "}
                            <button type="button" className="text-blue-600 hover:text-blue-800" onClick={() => fileInput.current?.click()}>
                                browse
                            </button>
                        </p>
                        {hint && <p className="text-xs text-stone-500">{hint}</p>}
                        <p className="text-xs text-stone-400">PDF, image, or Word documents, up to 30 MB each</p>
                    </div>
                )}
            </Dropzone>
            {value.length > 0 && (
                <ul className="space-y-1 text-sm">
                    {value.map((file, i) => (
                        <li key={`${file.name}-${i}`} className="flex items-center gap-2">
                            <PaperClipIcon className="h-4 w-4 shrink-0 text-stone-500" />
                            <span className="line-clamp-1">{file.name}</span>
                            <span className="whitespace-pre text-stone-500">({humanizeFileSize(file.size)})</span>
                            <button
                                type="button"
                                className="ml-auto rounded p-1 text-stone-500 hover:bg-stone-100 hover:text-red-600"
                                onClick={() => onChange(value.filter((_, index) => index !== i))}
                                aria-label={`Remove ${file.name}`}
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/** Sends supporting documents for a change that was saved directly (not queued as a pending change) */
export function uploadSupportingDocuments(accountId: string, files: File[], description: string) {
    return api.equipment.uploadSupportingDocuments(accountId, files, description).catch((error) => {
        console.error(error);
        useSnackbar.add(`${description} was submitted, but its supporting documents could not be uploaded.`, {
            variant: "warning",
            persist: true,
        });
    });
}
